// Game plays — which title a player played, when and for how long (F-CAT-04; spec/01 "Play record", spec/04 §4
// `game_play`, spec/10 §7.3, spec/13 §8, spec/17 R-04).
//
//   POST /v1/station/game-plays                                          station-authenticated
//   GET  /v1/station/sessions/current/games                              station-authenticated
//   GET  /v1/organizations/{o}/players/{playerId}/games                  players:read
//   GET  /v1/organizations/{o}/venues/{v}/reports/games-played           reports:read
//
// **What counts as playing.** The station matches running processes against each library title's detection
// rules (spec/10 §7.3: process names, an optional path pattern, an optional regex over the image path; a title
// with none falls back to its exe recipe's file name). A play is the time a title's process runs **while a session
// is in play on the station** (the `in_session` mode): it starts when the process is first seen, and stops when the
// process exits, the screen is locked or the session ends — so a game left running behind the lock screen does not
// count. It is process time, not foreground time: a game minimised to answer Discord is still being played.
// Plays shorter than a minute are dropped on the station (a launcher's helper that flashes up is not a play).
//
// **Delivery.** The station queues finished plays durably (a reboot keeps them) and posts them in batches whenever
// it can reach the api; each carries the station's own uuid, so a batch delivered twice stores each play once. The
// server attributes a play to the session's player — never to a player the station names — and drops a play whose
// session is not this station's organisation's.
//
// **Whose data.** A play is the player's personal data at this organisation (spec/20 §6): it is read by the
// organisation's staff through the player record (`players:read`) and in aggregate in the venue's reports
// (`reports:read`), and by the player themselves at the kiosk of a session they are in. Retention follows the
// session's (spec/04 §13: 25 months hot).
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { GamePlayIdSchema, LibraryEntryIdSchema, PlayerIdSchema, SessionIdSchema, StationIdSchema, VenueIdSchema } from "../ids/schema.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, jsonResponse, problemResponse } from "../openapi/responses.js";
import { PLAYERS_READ, REPORTS_READ } from "../permissions/index.js";
import { IsoDateTimeSchema, LocalDateSchema } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema, VenueScopeParamsSchema } from "../primitives/scope.js";
import { PERMISSION_EXTENSION } from "./audit.js";
const STATION_AUTHENTICATION = 'Station-authenticated: `Authorization: Bearer <device token>` (spec/07 §5); a user session, a member or ' +
    'a player token, an expired token and a revoked device are each refused with `401`.';
/** The longest play the server keeps: a day (spec/17 M-11, "each capped at 24 h"). */
export const MAX_GAME_PLAY_SECONDS = 24 * 60 * 60;
/** The shortest play a station reports: a minute. Anything shorter is a helper process, not play. */
export const MIN_GAME_PLAY_SECONDS = 60;
/** At most this many plays in one batch. */
export const MAX_GAME_PLAYS_PER_BATCH = 200;
/**
 * The title a play is of: the catalogue game (`gam_…`) or the organisation's custom app (`app_…`) — the library
 * bundle entry's `gameId`, which carries either.
 */
export const GamePlayTitleIdSchema = z
    .string()
    .regex(/^(gam|app)_[0-7][0-9a-hjkmnp-tv-z]{25}$/)
    .openapi('GamePlayTitleId', { description: 'The title played: a catalogue game (`gam_…`) or a custom app (`app_…`).', example: 'gam_01j9za8m4nq5c8e2hyt0v3w6xk' });
/**
 * How the station knew the process was this title (F-CAT-04, spec/10 §7.3), strongest evidence first:
 *
 *   launch      the kiosk launched the title and this process is the launch or one of its children;
 *   steam_app   Steam's `RunningAppID` (the player's own registry, reported by the agent) is the title's app id;
 *   install     the process runs from the title's install folder (Steam's appmanifest `installdir`, Epic's
 *               `InstallLocation`/`LaunchExecutable`, Riot's product install path);
 *   catalogue   the title's own detection rules (HQ's process names, path pattern, regex);
 *   recipe      the file an exe recipe starts;
 *   default     the curated default process names for a well-known title (`DEFAULT_TITLE_DETECTIONS`).
 */
export const GAME_PLAY_DETECTION_SOURCES = ['launch', 'steam_app', 'install', 'catalogue', 'recipe', 'default'];
export const GamePlayDetectedBySchema = z
    .enum(GAME_PLAY_DETECTION_SOURCES)
    .openapi('GamePlayDetectedBy', { description: 'How the station recognised the title’s process (spec/10 §7.3).' });
// ---------------------------------------------------------------------------------------------
// POST /v1/station/game-plays
// ---------------------------------------------------------------------------------------------
/** One finished play, as the station saw it. */
export const StationGamePlaySchema = z
    .object({
    /** The station's own id for this play (a uuid) — the idempotency key: a re-delivered play is stored once. */
    id: z.uuid(),
    /** The session it was played in. The server takes the player from the session, never from the station. */
    sessionId: SessionIdSchema,
    /** The library entry the title is offered as on this station. */
    entryId: LibraryEntryIdSchema,
    titleId: GamePlayTitleIdSchema,
    /** The title's name when it was played, so a report still names a title the venue has since removed. */
    name: z.string().trim().min(1).max(200),
    /** The process that matched, lower-case, without `.exe` (`valorant-win64-shipping`). */
    exe: z.string().trim().min(1).max(260),
    startedAt: IsoDateTimeSchema,
    endedAt: IsoDateTimeSchema,
    /** Seconds the title's process ran in play, from the station's monotonic clock; at most a day. */
    seconds: z.int().min(MIN_GAME_PLAY_SECONDS).max(MAX_GAME_PLAY_SECONDS),
    /** How the station recognised the process (2026-10-01; absent from an older station). */
    detectedBy: GamePlayDetectedBySchema.optional(),
})
    .openapi('StationGamePlay', { description: 'A title played on the station within a session, from start to stop (F-CAT-04).' });
export const StationGamePlaysSchema = z
    .object({ plays: z.array(StationGamePlaySchema).min(1).max(MAX_GAME_PLAYS_PER_BATCH) })
    .openapi('StationGamePlays', { description: 'A batch of finished plays from a station (F-CAT-04).' });
export const StationGamePlaysResultSchema = z
    .object({
    /** Plays stored now. */
    stored: z.int().min(0),
    /** Plays already stored by an earlier delivery of the same batch. */
    duplicates: z.int().min(0),
    /** Plays dropped for good: their session is not one of this station's organisation's. The station drops them too. */
    rejected: z.int().min(0),
})
    .openapi('StationGamePlaysResult', { description: 'What became of a batch of plays: stored, already stored, or dropped (F-CAT-04).' });
export const postStationGamePlaysRoute = createRoute({
    method: 'post',
    path: '/v1/station/game-plays',
    operationId: 'postStationGamePlays',
    tags: ['stations', 'sessions'],
    summary: 'The station reports the titles played in its sessions',
    description: `${STATION_AUTHENTICATION} Stores each finished play — the title, the session, when it started and stopped, and ` +
        'how long its process ran (F-CAT-04, spec/10 §7.3) — attributed to the session’s player. Idempotent on each ' +
        'play’s `id`. A play whose session is not the station’s organisation’s is dropped and counted as rejected; the ' +
        'station drops it too.',
    request: { body: { content: { [JSON_CONTENT_TYPE]: { schema: StationGamePlaysSchema } }, required: true } },
    responses: {
        200: jsonResponse(StationGamePlaysResultSchema, 'What became of each play.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// A title's totals, and one play — shared by the reads below
// ---------------------------------------------------------------------------------------------
/** One title's totals for a player (or a venue's report). */
export const GameTitleTotalSchema = z
    .object({
    titleId: GamePlayTitleIdSchema,
    /** The name the newest play recorded. */
    name: z.string(),
    /** Seconds played in total. */
    seconds: z.int().min(0),
    /** How many sessions it was played in. */
    sessions: z.int().min(0),
    lastPlayedAt: IsoDateTimeSchema,
})
    .openapi('GameTitleTotal', { description: 'A title’s play time in total, the sessions it was played in and when last (F-CAT-04).' });
// ---------------------------------------------------------------------------------------------
// GET /v1/station/sessions/current/games — the kiosk's "Your top games"
// ---------------------------------------------------------------------------------------------
/** At most this many titles in each of the kiosk's lists. */
export const KIOSK_GAMES_LIST_MAX = 6;
export const StationPlayerGameSchema = z
    .object({
    titleId: GamePlayTitleIdSchema,
    name: z.string(),
    /** Seconds played in total at this organisation. */
    seconds: z.int().min(0),
    lastPlayedAt: IsoDateTimeSchema,
})
    .openapi('StationPlayerGame', { description: 'A title the signed-in player has played at this organisation (F-CAT-04, spec/12 §7.3).' });
export const StationPlayerGamesSchema = z
    .object({
    /** Most played first, by total time. */
    top: z.array(StationPlayerGameSchema).max(KIOSK_GAMES_LIST_MAX),
    /** Most recently played first, one row per title. */
    recent: z.array(StationPlayerGameSchema).max(KIOSK_GAMES_LIST_MAX),
})
    .openapi('StationPlayerGames', { description: 'The signed-in player’s top and recently played titles at this organisation, for the kiosk (F-CAT-04).' });
export const getStationSessionGamesRoute = createRoute({
    method: 'get',
    path: '/v1/station/sessions/current/games',
    operationId: 'getStationSessionGames',
    tags: ['stations', 'sessions'],
    summary: 'The playing player’s top and recent titles, for the kiosk',
    description: `${STATION_AUTHENTICATION} The player of this station’s active session — the station names no player — and the ` +
        'titles they have played at this organisation: most played and most recent (F-CAT-04, spec/12 §7.3). `404` when ' +
        'the station has no active session.',
    responses: {
        200: jsonResponse(StationPlayerGamesSchema, 'The player’s titles.'),
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('This station has no active session.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// GET /v1/organizations/{o}/players/{playerId}/games — the player drawer's Games tab
// ---------------------------------------------------------------------------------------------
/** At most this many titles in the player's totals, and plays in the newest list. */
export const PLAYER_GAMES_TITLES_MAX = 50;
export const PLAYER_GAMES_RECENT_MAX = 25;
export const PlayerGamePlaySchema = z
    .object({
    id: GamePlayIdSchema,
    venueId: VenueIdSchema,
    sessionId: SessionIdSchema,
    /** The PC's number at its venue, or null when the station is gone. */
    stationNumber: z.int().min(1).nullable(),
    titleId: GamePlayTitleIdSchema,
    name: z.string(),
    startedAt: IsoDateTimeSchema,
    endedAt: IsoDateTimeSchema,
    seconds: z.int().min(0),
})
    .openapi('PlayerGamePlay', { description: 'One title the player played, from start to stop (F-CAT-04).' });
export const PlayerGamesSchema = z
    .object({
    /** Seconds played across every title. */
    totalSeconds: z.int().min(0),
    /** Per title, most played first. */
    titles: z.array(GameTitleTotalSchema).max(PLAYER_GAMES_TITLES_MAX),
    /** The newest plays first. */
    recent: z.array(PlayerGamePlaySchema).max(PLAYER_GAMES_RECENT_MAX),
})
    .openapi('PlayerGames', { description: 'What a player played at this organisation: per-title totals and the newest plays (F-CAT-04, spec/13 §8).' });
export const PlayerGamesParamsSchema = OrganizationScopeParamsSchema.extend({ playerId: PlayerIdSchema }).openapi('PlayerGamesParams');
export const PlayerGamesQuerySchema = z
    .object({
    venueId: VenueIdSchema.optional().openapi({
        description: 'Only this venue’s plays. A venue you do not work at is `404`. Without it: every venue you work at.',
    }),
})
    .strict()
    .openapi('PlayerGamesQuery');
export const listPlayerGamesRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/players/{playerId}/games',
    operationId: 'listPlayerGames',
    tags: ['players'],
    summary: 'What the player played here',
    description: 'The titles the player played at this organisation’s venues you work at — total time, sessions and when last, ' +
        'most played first — and the newest plays (F-CAT-04, spec/13 §8). A player the organisation has never dealt with ' +
        'is `404`. Requires `players:read`.',
    [PERMISSION_EXTENSION]: PLAYERS_READ,
    request: { params: PlayerGamesParamsSchema, query: PlayerGamesQuerySchema },
    responses: {
        200: jsonResponse(PlayerGamesSchema, 'The player’s titles and newest plays.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required'),
        404: problemResponse('No such player among the organisation’s players.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// GET /v1/organizations/{o}/venues/{v}/reports/games-played — the venue's most-played titles
// ---------------------------------------------------------------------------------------------
/** The longest range one report reads: a quarter. */
export const GAMES_PLAYED_MAX_DAYS = 92;
export const GamesPlayedQuerySchema = z
    .object({
    /** The first business day, in the venue's timezone. */
    from: LocalDateSchema,
    /** The last business day, inclusive. At most 92 days after `from`. */
    to: LocalDateSchema,
})
    .strict()
    .openapi('GamesPlayedQuery');
export const GamesPlayedTitleSchema = z
    .object({
    titleId: GamePlayTitleIdSchema,
    name: z.string(),
    seconds: z.int().min(0),
    /** Sessions it was played in. */
    sessions: z.int().min(0),
    /** Distinct players who played it. */
    players: z.int().min(0),
})
    .openapi('GamesPlayedTitle', { description: 'A title’s play time over the report’s range (F-CAT-04, spec/17 R-04).' });
/** One title's seconds within a day or on a station. */
export const GamesPlayedShareSchema = z
    .object({ titleId: GamePlayTitleIdSchema, seconds: z.int().min(0) })
    .openapi('GamesPlayedShare', { description: 'A title’s seconds within one day or on one station (F-CAT-04).' });
export const GamesPlayedDaySchema = z
    .object({
    /** The venue's business day a play started on. */
    date: LocalDateSchema,
    seconds: z.int().min(0),
    /** Every title played that day, most played first. */
    titles: z.array(GamesPlayedShareSchema),
})
    .openapi('GamesPlayedDay', { description: 'Play time on one of the venue’s days, per title (F-CAT-04).' });
export const GamesPlayedStationSchema = z
    .object({
    stationId: StationIdSchema,
    /** The PC's number, or null when the station has since been removed. */
    number: z.int().min(1).nullable(),
    name: z.string().nullable(),
    seconds: z.int().min(0),
    /** The titles played on it, most played first. */
    titles: z.array(GamesPlayedShareSchema),
})
    .openapi('GamesPlayedStation', { description: 'Play time on one station, per title (F-CAT-04).' });
export const GamesPlayedReportSchema = z
    .object({
    from: LocalDateSchema,
    to: LocalDateSchema,
    /** The venue's IANA timezone the days are counted in. */
    timeZone: z.string(),
    totalSeconds: z.int().min(0),
    /** Distinct players who played anything. */
    players: z.int().min(0),
    /** Every title played, most played first. */
    titles: z.array(GamesPlayedTitleSchema),
    /** Every day of the range, oldest first — days with no play included, at zero. */
    days: z.array(GamesPlayedDaySchema),
    /** Every station anything was played on, most played first. */
    stations: z.array(GamesPlayedStationSchema),
})
    .openapi('GamesPlayedReport', { description: 'The venue’s most-played titles over a range: per title, per day and per PC (F-CAT-04, spec/17 R-04).' });
export const getGamesPlayedReportRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/reports/games-played',
    operationId: 'getGamesPlayedReport',
    tags: ['reports'],
    summary: 'Games played: the venue’s most-played titles',
    description: 'Hours per title, per day (the venue’s business days, by when a play started) and per PC, from the plays the ' +
        'venue’s stations detected (F-CAT-04, spec/17 R-04). At most 92 days. Requires `reports:read`.',
    [PERMISSION_EXTENSION]: REPORTS_READ,
    request: { params: VenueScopeParamsSchema, query: GamesPlayedQuerySchema },
    responses: {
        200: jsonResponse(GamesPlayedReportSchema, 'The report.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required'),
        404: problemResponse('No such venue in this organisation, or not one you can see.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=game-plays.js.map